/**
 * Notification-group links for the CMC Ops map.
 * One ring is one label on one home site. An empty home is not a Group.
 * A gateway star is not a group.
 */

/** Same rule as canonicalGroupKey / helpers._sibling_group_norm. */
function groupLabel(value) {
  let s = String(value == null ? "" : value).trim();
  if (!s) return "";
  try { s = s.normalize("NFC"); } catch { /* keep s */ }
  s = s.split(/\s+/).join(" ");
  try { return s.toLocaleLowerCase(); } catch { return s.toLowerCase(); }
}

function homeSite(device) {
  return String((device && (device.site_id || device.site)) || "").trim();
}

/** Stable key. Blank when the board has no label or no home. */
export function groupLinkKey(device) {
  const label = groupLabel(device && device.group);
  const site = homeSite(device);
  if (!label || !site) return "";
  return site + "\0" + label;
}

/**
 * @param {{ id: string, ll: [number, number]|null, group?: string, site?: string, site_id?: string }[]} devices
 * @param {string} [selectedId]
 * @returns {{ type: string, features: object[] }}
 */
export function buildGroupLinkFeatures(devices, selectedId) {
  const placed = (Array.isArray(devices) ? devices : []).filter((d) => {
    if (!d || !d.ll || !Array.isArray(d.ll) || d.ll.length !== 2) return false;
    if (!Number.isFinite(d.ll[0]) || !Number.isFinite(d.ll[1])) return false;
    return !!groupLinkKey(d);
  });
  const byKey = new Map();
  for (const d of placed) {
    const key = groupLinkKey(d);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(d);
  }

  const sel = String(selectedId || "").trim();
  let groups = [...byKey.entries()];
  if (sel) {
    const hit = placed.find((d) => d.id === sel);
    const key = hit ? groupLinkKey(hit) : "";
    groups = key && byKey.has(key) ? [[key, byKey.get(key)]] : [];
  }

  const features = [];
  for (const [, pts] of groups) {
    if (pts.length < 2) continue;
    const ring = ringOrder(pts);
    const site = homeSite(ring[0]);
    const label = groupLabel(ring[0] && ring[0].group);
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (a.ll[0] === b.ll[0] && a.ll[1] === b.ll[1]) continue;
      features.push({
        type: "Feature",
        properties: { site, group: label, a: a.id, b: b.id },
        geometry: { type: "LineString", coordinates: [a.ll, b.ll] },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

/** Angular sort around the centroid. Two boards stay a single segment. */
function ringOrder(pts) {
  const list = pts.slice();
  if (list.length <= 2) return list;
  let sumLng = 0;
  let sumLat = 0;
  for (const p of list) {
    sumLng += p.ll[0];
    sumLat += p.ll[1];
  }
  const cLng = sumLng / list.length;
  const cLat = sumLat / list.length;
  list.sort((p, q) => {
    const ap = Math.atan2(p.ll[1] - cLat, p.ll[0] - cLng);
    const aq = Math.atan2(q.ll[1] - cLat, q.ll[0] - cLng);
    return ap - aq;
  });
  return list;
}
